import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { isConfigFlagEnabled } from '../config';
import { ReloadInstanceProvider } from '../context/reloadInstance/reloadInstanceContext';
import { delay, getTopWindow, isTestEnvironment } from '../utils/utils';
import { AppInstanceDimensionsWrapper } from './appInstanceDimensionsWrapper';
import { ContextProviderWrapper } from './contextProviderWrapper';
import { SceneWrapper } from './sceneWrapper';
function getRenderDelayMs(instanceId) {
    return (instanceId + 1) * 500;
}
function getInitialRenderedState(numInstances) {
    if (!isConfigFlagEnabled('staggerAppInstanceLoads')) {
        return true;
    }
    // never stagger if only there's only one instance total
    return !numInstances;
}
export const AppInstance = ({ instanceId, numInstances, widthRatio, heightRatio }) => {
    /**
     * To reduce issues with race conditions that can happen when auto-logging in multiple player accounts at once, we can stagger the loading of each app instance.
     * TODO: Remove this hack when localhost tests run reliably without staggering app instance loads.
     */
    const [isRendered, setIsRendered] = useState(getInitialRenderedState(numInstances));
    const isMounted = useMountedState();
    // create/remove container for this instance's test hook functions:
    useEffect(() => {
        if (isTestEnvironment()) {
            const topWindow = getTopWindow();
            topWindow.instanceFns = topWindow.instanceFns || [];
            topWindow.instanceFns[instanceId] = topWindow.instanceFns[instanceId] || {};
            return function cleanup() {
                if (topWindow.instanceFns) {
                    delete topWindow.instanceFns[instanceId];
                }
            };
        }
    }, [instanceId]);
    useEffect(() => {
        if (isRendered) {
            return;
        }
        const renderDelayMs = getRenderDelayMs(instanceId);
        if (!renderDelayMs) {
            setIsRendered(true);
        }
        async function effect() {
            await delay(renderDelayMs);
            if (!isMounted) {
                return;
            }
            setIsRendered(true);
        }
        effect();
    }, [isRendered, instanceId, isMounted]);
    if (!isRendered) {
        return null;
    }
    return (_jsx(ReloadInstanceProvider, { instanceId: instanceId, numInstances: numInstances, children: _jsx(ContextProviderWrapper, { instanceId: instanceId, numInstances: numInstances, children: _jsx(AppInstanceDimensionsWrapper, { children: _jsx(SceneWrapper, { widthRatio: widthRatio, heightRatio: heightRatio }) }) }) }));
};
